// Inventory.js

import React, { useState, useEffect, useCallback } from 'react';
import logger from '../../utils/logger';
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  onSnapshot,
  where,
  query,
  increment,
  arrayUnion,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../Auth/Auth';
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import AllocationForm from './AllocationForm';
import InventoryForm from './InventoryForm';
import InventoryList from './InventoryList';
import { Card, Spinner, Modal } from '../UI';

const Inventory = () => {
  const [inventoryItems, setInventoryItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [jobCards, setJobCards] = useState([]);
  const [showAllocationModal, setShowAllocationModal] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [allocation, setAllocation] = useState({
    itemId: '',
    quantity: 0,
    purpose: '',
    employeeId: '',
    jobCardId: '',
  });
  const [editItem, setEditItem] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  const { role, loading } = useAuth();
  const navigate = useNavigate();

  // Fetch Inventory Items
  const fetchInventoryItems = async () => {
    const querySnapshot = await getDocs(collection(db, 'inventory'));
    setInventoryItems(querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
  };

  // Fetch Employees
  const fetchEmployees = async () => {
    const querySnapshot = await getDocs(collection(db, 'employees'));
    setEmployees(querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
  };

  // Fetch Job Cards
  const fetchJobCards = async () => {
    const q = query(
      collection(db, 'jobs'),
      where('approvalStatus', '==', 'Yes'),
      where('status', '!=', 'Closed')
    );
    const querySnapshot = await getDocs(q);
    setJobCards(querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
  };

  useEffect(() => {
    fetchInventoryItems();
    fetchEmployees();
    fetchJobCards();
  }, []);

  // Automatic Inventory Update from Procurements (Updated to work with NEW system)
  useEffect(() => {
    const q = query(collection(db, 'procurements'), where('status', '==', 'Collected'));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      querySnapshot.docChanges().forEach((change) => {
        if (change.type === 'added' || change.type === 'modified') {
          const procurementData = change.doc.data();
          const procurementId = change.doc.id;

          if (procurementData.cartItems && Array.isArray(procurementData.cartItems)) {
            procurementData.cartItems.forEach(async (item) => {
              try {
                // ===== UPDATE NEW SYSTEM (stock + movements) =====
                // Check if item exists in NEW stock collection by partNumber
                const stockQuery = query(
                  collection(db, 'stock'),
                  where('partNumber', '==', item.partNumber || item.description)
                );
                const stockSnapshot = await getDocs(stockQuery);

                let stockItemId = null;
                let previousQuantity = 0;

                if (!stockSnapshot.empty) {
                  // Update existing stock item
                  const stockDoc = stockSnapshot.docs[0];
                  stockItemId = stockDoc.id;
                  previousQuantity = stockDoc.data().quantity || 0;

                  await updateDoc(stockDoc.ref, {
                    quantity: increment(item.quantity),
                    unitCost: item.price || stockDoc.data().unitCost,
                    totalValue: increment((item.price || 0) * item.quantity),
                    lastUpdated: Timestamp.now(),
                  });
                } else {
                  // Add new stock item
                  const newStockRef = await addDoc(collection(db, 'stock'), {
                    itemName: item.description,
                    partNumber: item.partNumber || item.description,
                    quantity: item.quantity,
                    location: 'OPEN FLOOR', // Default location for procurements
                    unitCost: item.price || 0,
                    totalValue: (item.price || 0) * item.quantity,
                    type: 'part',
                    reorderLevel: 5,
                    description: item.description,
                    dateAdded: Timestamp.now(),
                    lastUpdated: Timestamp.now(),
                    status: 'active',
                  });
                  stockItemId = newStockRef.id;
                }

                // Create movement record for PURCHASE
                const newBalance = previousQuantity + item.quantity;
                await addDoc(collection(db, 'movements'), {
                  stockItemId: stockItemId,
                  itemCode: item.partNumber || item.description,
                  itemDescription: item.description,
                  movementType: 'IN',
                  transactionType: 'PURCHASE',
                  quantityIn: item.quantity,
                  quantityOut: 0,
                  balanceAfter: newBalance,
                  unitCost: item.price || 0,
                  totalValue: (item.price || 0) * item.quantity,
                  date: Timestamp.now(),
                  supplier: procurementData.supplier || 'Direct Purchase',
                  remarks: `From Procurement #${procurementData.procurementNumber || procurementId}`,
                  recordedBy: procurementData.createdBy || 'System',
                  location: 'OPEN FLOOR',
                  procurementId: procurementId,
                });

                // ===== MAINTAIN OLD SYSTEM (for backward compatibility) =====
                const inventoryQuery = query(
                  collection(db, 'inventory'),
                  where('partName', '==', item.description)
                );
                const inventorySnapshot = await getDocs(inventoryQuery);

                if (!inventorySnapshot.empty) {
                  const inventoryDoc = inventorySnapshot.docs[0];
                  await updateDoc(inventoryDoc.ref, {
                    quantity: increment(item.quantity),
                  });
                } else {
                  await addDoc(collection(db, 'inventory'), {
                    type: 'part',
                    partName: item.description,
                    costPrice: item.price,
                    quantity: item.quantity,
                    acquiredAt: procurementData.dateOfPurchase || new Date(),
                    registeredBy: procurementData.createdBy || 'System',
                    procurementId: procurementId,
                    supplier: procurementData.supplier,
                    purchasePrice: item.price,
                  });
                }

                fetchInventoryItems();
              } catch (error) {
                logger.error('Error updating inventory from procurement:', error);
                toast.error(`Error updating inventory for ${item.description}`);
              }
            });
          } else {
            logger.warn('No cartItems in procurementData:', procurementData);
          }
        }
      });
    });
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openAllocationModal = (itemId) => {
    setSelectedItemId(itemId);
    setShowAllocationModal(true);
  };

  const handleAllocationChange = useCallback((e) => {
    const { name, value } = e.target;
    setAllocation((prevAllocation) => ({
      ...prevAllocation,
      [name]: value,
    }));
  }, []);

  const handleAllocationSubmit = async (e) => {
    e.preventDefault();

    const selectedItem = inventoryItems.find((item) => item.id === allocation.itemId);
    const allocatedQuantity = parseInt(allocation.quantity, 10);

    if (allocatedQuantity > selectedItem.quantity) {
      toast.error('Allocated quantity exceeds available stock.');
      return;
    }

    try {
      // Deduct allocated quantity from inventory
      const itemRef = doc(db, 'inventory', allocation.itemId);
      await updateDoc(itemRef, {
        quantity: selectedItem.quantity - allocatedQuantity,
      });

      // Record allocation
      await addDoc(collection(db, 'allocations'), {
        ...allocation,
        allocatedAt: new Date(),
      });

      // If purpose is 'Job Card', update the job's tracker
      if (allocation.purpose === 'Job Card') {
        const jobRef = doc(db, 'jobs', allocation.jobCardId);
        await updateDoc(jobRef, {
          tracker: arrayUnion({
            date: new Date(),
            state: 'Parts Obtained',
          }),
        });
      }

      toast.success('Item allocated successfully.');

      // Reset allocation form
      setAllocation({
        itemId: '',
        quantity: 0,
        purpose: '',
        employeeId: '',
        jobCardId: '',
      });

      // Refresh inventory items
      fetchInventoryItems();
    } catch (error) {
      logger.error('Error allocating item:', error);
      toast.error('Error allocating item.');
    }
    setShowAllocationModal(false);
    setSelectedItemId(null);
  };

  const handleEdit = (item) => {
    setEditItem(item);
    setIsEditing(true);
  };

  const handleDelete = async (itemId) => {
    try {
      await deleteDoc(doc(db, 'inventory', itemId));
      toast.success('Inventory Item Deleted');
      fetchInventoryItems();
    } catch (error) {
      logger.error('Error deleting inventory item:', error);
      toast.error('Error deleting inventory item.');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Spinner size="lg" />
      </div>
    );
  }

  if (role !== 'Finance' && role !== 'Admin') {
    return navigate('/unauthorized', { replace: true });
  }

  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <ToastContainer />

      {/* Header */}
      <Card className="mb-6">
        <div className="px-6 py-4">
          <h1 className="text-2xl font-bold text-gray-900 mb-1">Inventory Management</h1>
          <p className="text-sm text-gray-600">Manage stock items, track quantities, and allocate inventory</p>
        </div>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-gradient-to-br from-primary-50 to-primary-100">
          <div className="p-4">
            <p className="text-sm text-primary-700 font-medium mb-1">Total Items</p>
            <p className="text-2xl font-bold text-primary-900">{inventoryItems.length}</p>
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-success-50 to-success-100">
          <div className="p-4">
            <p className="text-sm text-success-700 font-medium mb-1">In Stock</p>
            <p className="text-2xl font-bold text-success-900">
              {inventoryItems.filter(item => item.quantity > 5).length}
            </p>
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-warning-50 to-warning-100">
          <div className="p-4">
            <p className="text-sm text-warning-700 font-medium mb-1">Low Stock</p>
            <p className="text-2xl font-bold text-warning-900">
              {inventoryItems.filter(item => item.quantity > 0 && item.quantity <= 5).length}
            </p>
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-danger-50 to-danger-100">
          <div className="p-4">
            <p className="text-sm text-danger-700 font-medium mb-1">Out of Stock</p>
            <p className="text-2xl font-bold text-danger-900">
              {inventoryItems.filter(item => item.quantity === 0).length}
            </p>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Column - Forms */}
        <div className="lg:col-span-4">
          <Card>
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {isEditing ? 'Edit Item' : 'Add New Item'}
              </h3>
            </div>
            <div className="p-6">
              <InventoryForm
                fetchInventoryItems={fetchInventoryItems}
                editItem={editItem}
                setEditItem={setEditItem}
                setIsEditing={setIsEditing}
                isEditing={isEditing}
              />
            </div>
          </Card>
        </div>

        {/* Right Column - Inventory List */}
        <div className="lg:col-span-8">
          <Card>
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <h3 className="text-lg font-semibold text-gray-900">Inventory Items</h3>
            </div>
            <div className="p-6">
              <InventoryList
                inventoryItems={inventoryItems}
                openAllocationModal={openAllocationModal}
                handleEdit={handleEdit}
                handleDelete={handleDelete}
              />
            </div>
          </Card>
        </div>
      </div>

      {/* AllocationForm Modal */}
      <Modal
        isOpen={showAllocationModal}
        onClose={() => setShowAllocationModal(false)}
        title="Allocate Inventory Item"
      >
        <AllocationForm
          handleAllocationSubmit={handleAllocationSubmit}
          handleAllocationChange={handleAllocationChange}
          allocation={allocation}
          inventoryItems={inventoryItems}
          employees={employees}
          jobCards={jobCards}
          selectedItemId={selectedItemId}
          fetchInventoryItems={fetchInventoryItems}
          setShowAllocationModal={setShowAllocationModal}
        />
      </Modal>
    </div>
  );
};

export default Inventory;
